import clsx from "clsx";
import type React from "react";
import { type CSSProperties, forwardRef } from "react";
import {
  IconCheck,
  IconClose,
  IconCloseCircle,
  IconInfo,
  IconWarning,
} from "../icons";
import {
  contentWrapper,
  type NotificationBannerVariant,
  notificationBannerVariants,
  notificationBannerWrapper,
} from "./styles.css";

interface NotificationBannerProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: NotificationBannerVariant;
  onClose?: () => void;
  isOpen?: boolean;
}

const VARIANT_ICONS: Record<NotificationBannerVariant, React.ReactNode> = {
  success: <IconCheck />,
  warning: <IconWarning />,
  error: <IconCloseCircle />,
  info: <IconInfo />,
};

export const NotificationBanner = forwardRef(function NotificationBanner(
  {
    variant = "info",
    onClose,
    isOpen,
    children,
    className,
    ...props
  }: NotificationBannerProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const transform = isOpen ? "scaleY(1)" : "scaleY(0)";
  const pointerEvents = isOpen ? "auto" : "none";

  return (
    <div
      ref={ref}
      className={clsx(
        notificationBannerWrapper,
        notificationBannerVariants[variant],
        className,
      )}
      style={
        {
          transform,
          pointerEvents,
        } as CSSProperties
      }
      role={variant === "error" || variant === "warning" ? "alert" : "status"}
      aria-live={variant === "error" ? "assertive" : "polite"}
      {...props}
    >
      {VARIANT_ICONS[variant]}
      <div className={contentWrapper}>{children}</div>
      {!!onClose && (
        <IconClose
          className="close-icon"
          onClick={() => onClose()}
          aria-label="Fechar notificação"
        />
      )}
    </div>
  );
});

NotificationBanner.displayName = "NotificationBanner";
